import {
  type BigFraction,
  bigFractionFromNumber,
} from "@aprao/common/src/math/BigFraction.js";
import { FRACTION_ONE } from "@aprao/common/src/math/fraction.js";

import { containsForRange, hasBoundsForRange } from "./FrameRange.js";
import {
  computeItemRange,
  type TimelineItemAdapter,
  type TimelineStageAdapter,
} from "./TimelineStageAdapter.js";

// a sigmoid that for [0, 1] produces [0, 1]
const modifiedSigmoid = (x: number, steepness = 10) => {
  const offset = 1 / (1 + Math.exp(steepness * 0.5));
  const scale = 1 / (1 / (1 + Math.exp(-steepness * 0.5)) - offset);
  const baseSigmoid = 1 / (1 + Math.exp(-steepness * (x - 0.5)));
  return (baseSigmoid - offset) * scale;
};

const calculateSCurveRatioForMonth = (
  frameIndex: number,
  totalFrames: number,
  steepness = 10,
) => {
  const cumulativeStart = modifiedSigmoid(frameIndex / totalFrames, steepness);
  const cumulativeEnd = modifiedSigmoid(
    (frameIndex + 1) / totalFrames,
    steepness,
  );

  return cumulativeEnd - cumulativeStart;
};

const periodic = (params: {
  stage: TimelineStageAdapter;
  item: TimelineItemAdapter;
  frameIndex: number;
  framesIntervalLength: number;
}) => {
  const range = computeItemRange(params.stage, params.item);

  if (
    !hasBoundsForRange(range) ||
    !containsForRange(range, params.frameIndex)
  ) {
    return null;
  }

  const framesCount = range.upper - range.lower;
  const totalSteps = Math.ceil(framesCount / params.framesIntervalLength);

  const frameOffset = params.frameIndex - range.lower;

  return frameOffset % params.framesIntervalLength === 0 ?
      FRACTION_ONE.div(totalSteps)
    : null;
};

type DistributionMethodFn = (
  stage: TimelineStageAdapter,
  item: TimelineItemAdapter,
  frameIndex: number,
) => BigFraction | null;

export const distributionMethodsRegistry = {
  linear: (stage, item, frameIndex) => {
    const range = computeItemRange(stage, item);

    if (!hasBoundsForRange(range) || !containsForRange(range, frameIndex)) {
      return null;
    }

    const framesCount = range.upper - range.lower;

    return FRACTION_ONE.div(framesCount);
  },

  "s-curve": (stage, item, frameIndex) => {
    const range = computeItemRange(stage, item);

    if (!hasBoundsForRange(range) || !containsForRange(range, frameIndex)) {
      return null;
    }

    const framesCount = range.upper - range.lower;

    return bigFractionFromNumber(
      calculateSCurveRatioForMonth(frameIndex - range.lower, framesCount),
    );
  },

  quarterly: (stage, item, frameIndex) =>
    periodic({
      stage,
      item,
      frameIndex,
      framesIntervalLength: 3,
    }),

  manual: () => null,
} satisfies Record<string, DistributionMethodFn>;

export type DistributionMethodName = keyof typeof distributionMethodsRegistry;
